/**
 * Auth Store - 认证状态管理
 * 使用 Zustand 管理用户认证状态
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, TokenStorage } from '../services/auth'

/**
 * 认证状态接口
 */
interface AuthState {
  // 状态
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // 动作
  setUser: (user: User | null) => void
  setAuthenticated: (authenticated: boolean) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  
  // 登录/登出
  login: (user: User, token: string) => void
  logout: () => void
  
  // 初始化
  initialize: () => void
  clearError: () => void
}

/**
 * 认证 Store
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // 初始状态
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      
      // 动作
      setUser: (user) => set({ user }),
      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      
      // 登录
      login: (user, token) => {
        TokenStorage.set(token)
        TokenStorage.setUser(user)
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        })
      },
      
      // 登出
      logout: () => {
        TokenStorage.clear()
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })
      },
      
      // 初始化（从 localStorage 恢复状态）
      initialize: () => {
        const token = TokenStorage.get()
        const user = TokenStorage.getUser()
        
        if (token && user) {
          set({
            user,
            isAuthenticated: true,
            isLoading: false
          })
        } else {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false
          })
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // 只持久化必要的状态
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

export default useAuthStore

